import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ConsoleDrawer from '../components/ConsoleDrawer';
import ModuleCard from '../components/ModuleCard';
import { getModules } from '../lib/apiClient';
import { getStudyHub, getMyInsightsMini } from '../lib/studyApi';
import { useBrand } from '../context/BrandContext';

const StudentDashboard = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decksByModuleId, setDecksByModuleId] = useState({});
  const [insights, setInsights] = useState(null);
  const brand = useBrand();

  useEffect(() => {
    getModules().then((m) => setModules(m)).finally(() => setLoading(false));
    getStudyHub()
      .then((hub) => {
        const byModuleId = {};
        for (const d of (hub.decks || [])) {
          if (!d.module_id) continue;
          byModuleId[d.module_id] = d;
        }
        setDecksByModuleId(byModuleId);
      })
      .catch(() => setDecksByModuleId({}));
    getMyInsightsMini().then(setInsights).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" data-testid="student-dashboard">
      <Navbar subtitle="Student" />
      <ConsoleDrawer role="student">
      <main className="max-w-7xl mx-auto px-6 py-10 pl-16">
        <div className="mb-8 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Your Modules</h1>
            <p className="text-gray-500 mt-1" data-testid="student-dashboard-description">{brand.course_description}</p>
          </div>
        </div>
        {insights && (
          <Link
            to="/study/insights"
            data-testid="student-insight-tile"
            className="block mb-8 rounded-lg p-5 no-underline"
            style={{
              background: 'linear-gradient(135deg, rgba(252,211,89,0.25), white 60%)',
              border: '1px solid rgba(224,183,61,0.5)',
              color: '#0E2A47',
              boxShadow: '0 2px 8px rgba(224,183,61,0.15)',
            }}
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-yellow-700">
                  🧠 Your Recall/Connect
                </div>
                <div className="text-xl font-bold mt-1">
                  {insights.recall_mastery >= 70
                    ? 'You are in great shape'
                    : insights.recall_mastery > 0
                      ? `${insights.recall_mastery}% mastery — a short session tightens it`
                      : 'Start your first review — it takes a few minutes'}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {insights.streak_days > 0 && <span>🔥 {insights.streak_days}-day streak · </span>}
                  <span>🎯 {insights.recall_mastery}% recall</span>
                  <span> · 🌉 {insights.connect_strength}% connect</span>
                  <span> · 🥊 Rating {insights.peer_elo}</span>
                  <span> · {insights.duel_record.wins}W-{insights.duel_record.losses}L</span>
                </div>
              </div>
              <div className="text-sm font-bold text-yellow-800">
                Open your dashboard →
              </div>
            </div>
          </Link>
        )}
        {loading ? (
          <div className="text-gray-500">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="student-modules-grid">
            {modules.map((m) => (
              <ModuleCard
                key={m.id}
                module={m}
                viewerRolePath="/student"
                canEdit={false}
                studyDeck={decksByModuleId[m.id] || null}
              />
            ))}
          </div>
        )}
      </main>
      </ConsoleDrawer>
      {/* AI Tutor now lives inside ConsoleDrawer's tutor pill (voice + text).
          Legacy floating ChatPanel bubble removed to eliminate the double UI. */}
    </div>
  );
};

export default StudentDashboard;
